import type { DataBus } from '@ms-cloudpack/data-bus';
import PQueue from 'p-queue';
import { taskListSource, taskStatsSource } from '../data/busSources.js';
import type { Task, TaskEndDescription } from '../types/Task.js';
import type { TaskDescription } from '../types/TaskDescription.js';
import type { TaskList } from '../types/TaskList.js';
import type { TaskStats } from '../types/TaskStats.js';

interface TaskIssueStats extends Pick<TaskStats, 'totalErrors' | 'totalWarnings'> {
  /** Mapping from ID to count */
  taskWarnings: Record<string, number>;
  /** Mapping from ID to count */
  taskErrors: Record<string, number>;
}

/**
 * TaskRunner tracks the status of tasks, manages concurrency, allows reusing task results,
 * and sends notifications about task status to the data bus (currently just used for `start`).
 *
 * NOTE: To enable data bus notifications, you must call `connectToBus()`.
 * `createApiContext` does this with the `taskRunner` passed in.
 * (`createPartialApiContext` does not create or connect a data bus.)
 */
export class TaskRunner {
  private _bus: DataBus | undefined;
  private readonly _queue: PQueue;
  /** Stats about task errors/warnings, only updated if a bus is connected */
  private _taskIssues: TaskIssueStats;
  /**
   * Tasks that are currently either running or waiting. (Note that `PQeueue`'s definition of
   * "pending" is slightly different: it only counts tasks that are currently running.)
   */
  private readonly _pendingTasks: Map<string, Promise<unknown>>;
  /** Tasks that have completed */
  private readonly _completedTasks: Map<string, unknown>;
  /**
   * Task IDs where a re-run was requested while the task was running.
   * We let the current task finish, and then re-run it.
   */
  private readonly _requiresRerun: Set<string>;
  /** All previously-seen tasks (regardless of state) */
  private readonly _allTasks: Map<string, Task<unknown>>;

  constructor() {
    this._queue = new PQueue({ concurrency: 10 });
    this._taskIssues = { totalErrors: 0, totalWarnings: 0, taskWarnings: {}, taskErrors: {} };
    this._pendingTasks = new Map();
    this._completedTasks = new Map();
    this._requiresRerun = new Set();
    this._allTasks = new Map();
  }

  /**
   * Get current task stats. Public for testing.
   * @internal
   */
  get _taskStats(): Readonly<TaskStats> {
    return {
      status: this._pendingTasks.size ? 'pending' : 'idle',
      remainingTasks: this._pendingTasks.size,
      totalTasks: this._allTasks.size,
      totalErrors: this._taskIssues.totalErrors,
      totalWarnings: this._taskIssues.totalWarnings,
    };
  }

  /**
   * Connects the TaskRunner to the data bus. Tasks will still run without this, but they won't
   * send any notifications to a client (which only matters if a client is listening, e.g.
   * the browser while running `start`).
   */
  public connectToBus(bus: DataBus): void {
    this._bus = bus;
  }

  /**
   * Enqueue a task and return its result promise.
   *
   * If a task with the same `id` was already added, it returns either the previous result
   * (if completed) or the promise (if pending). Pass `options.rerun` to force re-running.
   *
   * (Note that any re-runs will use the *original* task function, not the new one.
   * If something has changed that requires a new task function, you should incorporate this
   * into the `id`.)
   */
  add<TReturn>(
    task: Task<TReturn>,
    options?: {
      rerun?: boolean;
      priority?: number;
    },
  ): Promise<TReturn> {
    const { name, id, execute } = task;
    const { rerun, priority } = options || {};

    if (!this._allTasks.has(id)) {
      this._allTasks.set(id, task as Task<unknown>);
    }

    // Check if we are already completed the task with that id.
    const action = this._completedTasks.get(id) as TReturn | undefined;
    if (action !== undefined && !rerun) {
      return Promise.resolve(action);
    }

    // Check if we are already running the task with that id.
    const pending = this._pendingTasks.get(id) as Promise<TReturn> | undefined;
    if (pending !== undefined) {
      if (rerun) {
        this._requiresRerun.add(id);
      }
      return pending;
    }

    const resultPromise: Promise<TReturn> = (async () => {
      let taskResult: TReturn | undefined;
      let taskError: unknown;

      try {
        do {
          // Delete from rerun list (if present).
          this._requiresRerun.delete(id);

          taskResult = await this._queue.add(execute, { priority });
        } while (this._requiresRerun.has(id));
      } catch (error) {
        taskError = error;
        // Re-throw the error, but `finally` will handle completion
        throw error;
      } finally {
        // Delete from pending list.
        this._pendingTasks.delete(id);

        this._reportEnd(task, taskResult, taskError);
      }

      // Extra check to ensure that the task returned a value.
      // It should never throw, if it does we have a bug.
      if (taskResult === undefined) {
        throw new Error(`Task "${name}:${id}" returned undefined, without throwing an error, which was unexpected.`);
      }

      // Update finished Tasks.
      this._completedTasks.set(id, taskResult);

      return taskResult;
    })();

    this._pendingTasks.set(id, resultPromise);
    const wasCompleted = this._completedTasks.delete(id);
    if (wasCompleted) {
      this._removeTaskIssues(task.id);
    }

    this._reportStart<TReturn>(task);

    return resultPromise;
  }

  /**
   * Dispose in-memory resources of all tasks and clear the TaskRunner's state.
   */
  // NOTE: This is also used by clearAllResults(), so if it's updated to do anything that
  // renders the TaskRunner unusable/finished, the common parts should be refactored out.
  dispose(): void {
    this._queue.clear();
    for (const task of this._allTasks.values()) {
      task.dispose?.();
    }
    this._allTasks.clear();
    this._pendingTasks.clear();
    this._completedTasks.clear();
    this._requiresRerun.clear();
    this._taskIssues = { totalErrors: 0, totalWarnings: 0, taskWarnings: {}, taskErrors: {} };
  }

  /**
   * Clear the result of a specific task (could involve deleting files from disk) and remove
   * it from the TaskRunner's task records.
   */
  async clearResult(id: string): Promise<void> {
    const task = this._allTasks.get(id);
    task?.dispose?.();
    await task?.clear?.();

    this._allTasks.delete(id);
    this._requiresRerun.delete(id);
    this._pendingTasks.delete(id);
    this._completedTasks.delete(id);
    this._removeTaskIssues(id);

    if (this._bus) {
      const { tasks, index } = this._findFromTaskList(id);
      if (index !== -1) {
        tasks.splice(index, 1);
        this._publishUpdates({ tasks, stats: true });
      }
    }
  }

  /**
   * Clear the results of all tasks (might include deleting files on disk) and clear the
   * TaskRunner's task records.
   *
   * (To only dispose in-memory resources, use `dispose()`.)
   */
  async clearAllResults(): Promise<void> {
    // Publish the empty task list first since deleting things might take awhile.
    this._publishUpdates({ tasks: [] });

    for (const task of this._allTasks.values()) {
      task.dispose?.();
      await task.clear?.();
    }
    this.dispose();

    // Publish new stats after clearing everything (otherwise the calculated values will be wrong)
    this._publishUpdates({ stats: true });
  }

  /**
   * Pauses the TaskRunner (for testing).
   * @internal
   */
  _pause(): void {
    this._queue.pause();
  }

  /**
   * Starts or resumes the TaskRunner (for testing).
   * @internal
   */
  _start(): void {
    this._queue.start();
  }

  /** Remove any records of previous issues with this task from the totals */
  private _removeTaskIssues(id: string): void {
    this._taskIssues.totalErrors -= this._taskIssues.taskErrors[id] || 0;
    this._taskIssues.totalWarnings -= this._taskIssues.taskWarnings[id] || 0;
    delete this._taskIssues.taskErrors[id];
    delete this._taskIssues.taskWarnings[id];
  }

  /**
   * Internal method to report the start of a task to the bus.
   */
  private _reportStart<TReturn>(task: Task<TReturn>): void {
    if (!this._bus || !task.getStartDescription) return;

    const { tasks, index } = this._findFromTaskList(task.id);

    const taskDesc: TaskDescription = {
      id: task.id,
      name: task.name,
      ...task.getStartDescription(),
      status: 'pending',
      startTime: Date.now(),
    };
    if (index === -1) {
      tasks.push(taskDesc);
    } else {
      tasks[index] = taskDesc;
    }

    this._publishUpdates({ tasks, stats: true });
  }

  /**
   * Internal method to report the end of a task to the bus.
   */
  private _reportEnd<TReturn>(task: Task<TReturn>, taskResult: TReturn | undefined, taskError: unknown): void {
    if (!this._bus || !task.getEndDescription) return;

    // Replace the usual description with an error if relevant
    const description: TaskEndDescription = taskError
      ? {
          errors: [
            {
              source: 'running task',
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              text: taskError instanceof Error ? taskError.stack || taskError.message : String(taskError),
            },
          ],
        }
      : task.getEndDescription(taskResult);
    const errorCount = description.errors?.length || 0;
    const warningCount = description.warnings?.length || 0;
    if (errorCount) {
      this._taskIssues.taskErrors[task.id] = errorCount;
      this._taskIssues.totalErrors += errorCount;
    }
    if (warningCount) {
      this._taskIssues.taskWarnings[task.id] = warningCount;
      this._taskIssues.totalWarnings += warningCount;
    }

    const { tasks, index } = this._findFromTaskList(task.id);
    const startDescription = tasks[index];

    if (startDescription) {
      const now = Date.now();
      tasks[index] = {
        ...startDescription,
        ...description,
        status: 'complete',
        duration: now - startDescription.startTime,
        lastUpdated: now,
      };

      this._publishUpdates({ tasks, stats: true });
    }
  }

  /** Get the task list from the data bus, and find the task's index. */
  private _findFromTaskList(id: string): { tasks: TaskDescription[]; index: number } {
    const tasks = this._bus?.getData<TaskList>(taskListSource.path)?.tasks || [];
    const index = tasks.findIndex((t) => t.id === id);
    return { tasks, index };
  }

  /**
   * Publish tasks and/or stats to the data bus.
   */
  private _publishUpdates(params: { tasks?: TaskDescription[]; stats?: boolean }): void {
    const { tasks, stats } = params;
    // Publish using .path for easier testing
    stats && this._bus?.publish<TaskStats>(taskStatsSource.path, this._taskStats);
    tasks && this._bus?.publish<TaskList>(taskListSource.path, { tasks });
  }
}
